import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import Img from 'gatsby-image'
import oliwacentralLogo from '../../../assets/oliwacentral-logo'
import { colors, s, alpha } from '../../../style'
import ReactMarkdown from 'react-markdown'
import { CarouselProvider, Slider, Slide, DotGroup } from 'pure-react-carousel'
import { sDotGroup } from '../Intro/Intro'
import Table from '../Table/Table'
import Panorama from '../Panorama/Panorama'

export const Line = ({
  width = 600,
  rotate = false,
  center = false,
  top,
  left,
  right,
  bottom,
  noMobile,
}) => (
  <div
    className="stylish-line"
    css={[
      noMobile && { [s.sm_down]: { display: 'none' } },
      top && { top: top, [s.sm_down]: { top: top / 2 } },
      bottom && { bottom: bottom, [s.sm_down]: { bottom: bottom / 2 } },
      left && { left: left, [s.sm_down]: { left: left / 2 } },
      right && { right: right, [s.sm_down]: { right: right / 2 } },
      center && { transform: 'translate(-50%, -50%)' },
      {
        position: 'absolute',
        height: 1,
        width: width,
        background: `linear-gradient(to right, ${colors.mainLighter.concat(
          alpha[0]
        )} 0%,  ${colors.mainLighter.concat(
          alpha[100]
        )} 50%, ${colors.mainLighter.concat(alpha[0])} 100%)`,
      },
      rotate && {
        width: 1,
        height: width,
        background: `linear-gradient(to bottom, ${colors.mainLighter.concat(
          alpha[0]
        )} 0%,  ${colors.mainLighter.concat(
          alpha[100]
        )} 50%, ${colors.mainLighter.concat(alpha[0])} 100%)`,
      },
    ]}
  />
)

export const TextContainer = ({ children, large, extraCss }) => {
  return (
    <div css={[sTextContainer, large && sTextContainer.large, extraCss]}>
      {children}
    </div>
  )
}

export const Headline = ({ title, desc, extraCss, noShadow, success }) => {
  return (
    <div
      id={success ? 'send_form_thank_you' : 'send_form_response'}
      css={[
        sHeadline,
        noShadow && { '.headline-title': { textShadow: 'none' } },
        extraCss,
      ]}>
      <h2 className="headline-title">{title}</h2>
      {desc && <p className="headline-desc">{desc}</p>}
    </div>
  )
}

export const Carousel = ({ data, video, image }) => {
  return (
    <CarouselProvider
      visibleSlides={1}
      step={1}
      totalSlides={data.length}
      naturalSlideWidth={1280}
      naturalSlideHeight={720}
      isPlaying={image ? true : false}
      infinite
      interval={3300}
      touchEnabled={false}
      dragEnabled={false}
      css={{
        position: 'relative',
        border: 'none',
        outline: 'none',
        '.carousel__slide-focus-ring': { display: 'none' },
        boxShadow: `4px 8px 32px ${colors.black.concat(alpha[30])}`,
      }}>
      <Slider>
        {image &&
          data.map(({ image, title }, id) => (
            <Slide key={id} index={id} css={{ position: 'relative' }}>
              <Img
                fluid={image.childImageSharp.fluid}
                alt={title || `image${id}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                imgStyle={{
                  objectFit: 'cover',
                  objectPosition: 'bottom center',
                }}
              />
              <div css={sDimmer} />
              <div
                css={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  padding: '2rem 0rem',
                  [s.xs]: {
                    padding: '1rem 2rem',
                  },
                }}>
                <span css={sCarouselTitle}>{title}</span>
              </div>
            </Slide>
          ))}
        {video &&
          data.map(({ youtube, title }, id) => (
            <Slide
              key={id}
              index={id}
              css={{
                position: 'relative',
                width: 'inherit',
                height: 'inherit',
              }}>
              {/* <YouTubeFrame id={'elZ6YJu_HgA'} /> */}
              <div
                css={{
                  position: 'relative',
                  paddingBottom: '56.25%' /* 16:9 */,
                  height: 0,
                  iframe: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    outline: 'none',
                    border: 'none',
                  },
                }}>
                <iframe
                  title={title}
                  width="inherit"
                  height="inherit"
                  allowFullScreen
                  mozallowfullscreen="mozallowfullscreen"
                  msallowfullscreen="msallowfullscreen"
                  oallowfullscreen="oallowfullscreen"
                  webkitallowfullscreen="webkitallowfullscreen"
                  src={`https://www.youtube.com/embed/${youtube}?&fs=1&modestbranding=1&autoplay=0&loop=1&iv_load_policy=3`}
                />
              </div>
              {/* <div
                css={{
                  width: 'inherit',
                  height: 'inherit',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}>
      
                <div css={sDimmer} />
                <span css={sCarouselTitle}>{title}</span>
              </div> */}
            </Slide>
          ))}
      </Slider>
      {data?.length > 1 && <DotGroup css={[sDotGroup, sDotGroupNew]} />}
    </CarouselProvider>
  )
}

const Presentation = ({ children }) => {
  const {
    present: {
      frontmatter: {
        intro_present_index,
        features_present_index,
        apartments_present_index,
        video_present_index,
        drones_present_index,
      },
    },
    rzut,
  } = useStaticQuery(graphql`
    query {
      present: markdownRemark(
        fileAbsolutePath: { regex: "/content/index/present/present.md/" }
      ) {
        frontmatter {
          intro_present_index {
            title
            body
            images {
              title
              image {
                childImageSharp {
                  fluid(
                    maxWidth: 1280
                    quality: 100
                    toFormat: JPG
                    jpegProgressive: true
                  ) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
          features_present_index {
            title
            desc
            features {
              feature
            }
          }
          apartments_present_index {
            title
            desc
            apartments {
              title
              subtitle
              desc
              image {
                childImageSharp {
                  fluid(
                    maxWidth: 1280
                    quality: 100
                    toFormat: JPG
                    jpegProgressive: true
                  ) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
          video_present_index {
            title
            desc
            videos {
              title
              youtube
            }
          }
          drones_present_index {
            title
            images {
              title
              image {
                childImageSharp {
                  fluid(
                    maxWidth: 1280
                    quality: 100
                    toFormat: JPG
                    jpegProgressive: true
                  ) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
      }
      rzut: file(absolutePath: { regex: "/images/rzut.jpg/" }) {
        childImageSharp {
          fluid(
            maxWidth: 1280
            quality: 100
            toFormat: JPG
            jpegProgressive: true
          ) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)

  // console.log(video_present_index)

  return (
    <div css={sOuter} id="oliwa-central">
      <Line top={128} left={-64} width={900} noMobile />
      <Line top={-64} left={128} width={900} rotate noMobile />
      <Line bottom={128} right={-64} width={1200} noMobile />
      <Line bottom={-64} right={256} width={900} rotate noMobile />
      <div css={sLogo}>{oliwacentralLogo}</div>
      <section css={sInnerSection}>
        <Headline
          title={intro_present_index.title}
          desc={intro_present_index.desc}
        />
        <div css={{ position: 'relative' }}>
          <TextContainer
            extraCss={{
              [s.xs]: { marginBottom: '4rem' },
            }}>
            <Line bottom={-32} right={-64} width={900} center />
            {/* <Line bottom={0} right={-128} width={900} rotate /> */}
            <ReactMarkdown>{intro_present_index.body}</ReactMarkdown>
          </TextContainer>
        </div>
        <Carousel data={intro_present_index.images} image />
      </section>
      <div css={{ position: 'relative' }}>
        <Line top={-128} left={-640} width={1200} noMobile />
        <Line top={-480} left={-160} width={900} noMobile rotate />
        <Line top={-1280} right={-160} width={900} noMobile rotate />
        <Line bottom={128} right={-320} width={1200} noMobile />
        <Line bottom={-256} right={-128} width={900} rotate noMobile />
        <section css={sInnerSection}>
          <Headline
            title={features_present_index.title}
            desc={features_present_index.desc}
          />
          {features_present_index.features.map(({ feature }, id) => (
            <TextContainer large key={id}>
              {feature}
            </TextContainer>
          ))}
          <div id="oferta" />
          <Img
            fluid={rzut.childImageSharp.fluid}
            alt={`rzut`}
            style={{
              width: '100%',
              maxWidth: 1280,
              apsectRatio: '16/9',
            }}
            imgStyle={{
              objectFit: 'cover',
              objectPosition: 'center center',
            }}
          />
          <Table />
        </section>
      </div>
      {children}
      <section css={{ position: 'relative' }}>
        <Line top={-128} left={'50%'} width={1280} noMobile center />
        <div
          css={{
            width: '100%',
            maxWidth: 1280,
            margin: '0 auto',

            padding: '0 4rem',
            [s.sm_down]: {
              padding: 0,
            },
            boxSizing: 'border-box',
          }}>
          <Headline
            title={apartments_present_index.title}
            desc={apartments_present_index.desc}
          />
        </div>
      </section>
      <div css={sApartments}>
        {apartments_present_index.apartments.map(
          ({ title, subtitle, desc, image }, id) => (
            <div key={id} css={sApartment}>
              <div css={sApartmentImageWrapper}>
                <Img
                  fluid={image.childImageSharp.fluid}
                  alt={title}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
              <div css={sApartment.container}>
                <h3
                  css={{
                    fontSize: '1.375rem',
                    fontWeight: 300,
                    margin: '2rem 0 0.5rem',
                    [s.xs]: {
                      width: '90%',
                    },
                  }}>
                  {title}
                </h3>
                <h4
                  css={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    marginBottom: '2rem',
                  }}>
                  {subtitle}
                </h4>
                <p
                  css={{
                    fontSize: '0.75rem',
                    fontWeight: 300,
                    width: '90%',
                    opacity: 0.6,
                    [s.xs]: {
                      width: '100%',
                    },
                  }}>
                  {desc}
                </p>
              </div>
            </div>
          )
        )}
      </div>
      <div css={{ position: 'relative' }}>
        <Line top={-128} left={-320} width={900} noMobile />
        <Line top={-320} left={-160} width={900} noMobile rotate />

        <section css={sInnerSection}>
          <Headline
            title={video_present_index.title}
            desc={video_present_index.desc}
          />
          {/* <YouTubeFrame
          id={video_present_index.videos[0].youtube}
          // id={'elZ6YJu_HgA'}
        /> */}
          <Carousel video data={video_present_index.videos} />
          <div css={{ marginTop: '4rem' }} />
          <Panorama />
          <div css={{ marginBottom: '4rem' }} />
        </section>
      </div>
      <section css={sInnerSection}>
        <Headline
          title={drones_present_index.title}
          desc={drones_present_index.desc}
        />
        <Carousel data={drones_present_index.images} image />
      </section>
    </div>
  )
}

const sApartments = {
  display: 'flex',
  flexDirection: 'row',
  marginBottom: '16rem',

  [s.sm_down]: {
    marginBottom: '6rem',
  },
  [s.md]: {
    marginBottom: '10rem',
  },
  [s.lg]: {
    marginBottom: '16rem',
  },
  [s.xs]: {
    flexDirection: 'column',
  },
}

const sApartment = {
  display: 'flex',
  flexDirection: 'column',

  margin: '0 1rem',
  [s.xs]: { width: '100vw', margin: 0 },
  [s.sm]: {
    width: '28vw',
  },
  [s.lg]: {
    width: '25vw',
  },
  container: {
    [s.xs]: { padding: '0rem 2rem 4rem' },
  },
}

const sApartmentImageWrapper = {
  position: 'relative',
  width: '100%',
  height: '30vw',
  [s.sm]: {
    height: '30vw',
  },
  [s.lg]: {
    height: '25vw',
  },
  [s.xs]: { height: 'calc(100vw - 64px)' },
  boxShadow: `4px 8px 48px ${colors.black.concat(alpha[30])}`,
}

const sOuter = {
  position: 'relative',
  background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' opacity='0.04' width='150' height='150' viewBox='0 0 100 100'%3E%3Cdefs%3E%3CclipPath id='clip-Artboard_10'%3E%3Crect width='100' height='100'/%3E%3C/clipPath%3E%3C/defs%3E%3Cg id='Artboard_10' clip-path='url(%23clip-Artboard_10)'%3E%3Cpath d='M-4605.7-5074.678h-4.357v-3.941A5.512,5.512,0,0,1-4613-5083.5a5.509,5.509,0,0,1,2.941-4.878v-3.943h4.357a5.524,5.524,0,0,1,5.2-3.68,5.521,5.521,0,0,1,5.2,3.68h4.357v3.943A5.507,5.507,0,0,1-4588-5083.5a5.511,5.511,0,0,1-2.941,4.882v3.941h-4.357a5.515,5.515,0,0,1-5.2,3.677A5.523,5.523,0,0,1-4605.7-5074.678Zm-2.151-13.065a4.387,4.387,0,0,0-2.943,4.242,4.39,4.39,0,0,0,2.943,4.246v2.373h2.956a4.439,4.439,0,0,0,4.4,4.046,4.434,4.434,0,0,0,4.4-4.046h2.956v-2.373a4.39,4.39,0,0,0,2.943-4.246,4.387,4.387,0,0,0-2.943-4.242v-2.377h-2.956a4.433,4.433,0,0,0-4.4-4.043,4.437,4.437,0,0,0-4.4,4.043h-2.956Zm7.3,10.847c-.245-.031-1.064-.962-.733-2.835.079-.441.166-.792.235-1.1.087-1.293-1.132-2.064-1.952-2.4-.333-.11.009.38-.567,1a1.253,1.253,0,0,1-1.678-.129,1.659,1.659,0,0,1-.224-1.845,1.57,1.57,0,0,1,1.61-.851c1.58-.01,2.7,1.543,3.112,2.866a3.532,3.532,0,0,0-.186-1.264c-.263-.861-1.326-1.6-1.326-3.507a4.171,4.171,0,0,1,1.756-3.155,4.2,4.2,0,0,1,1.756,3.155c0,1.9-1.073,2.646-1.336,3.507a3.532,3.532,0,0,0-.186,1.264c.41-1.323,1.532-2.866,3.112-2.866a1.571,1.571,0,0,1,1.61.851,1.659,1.659,0,0,1-.224,1.845,1.256,1.256,0,0,1-1.728.129c-.555-.651-.233-1.142-.555-1a2.853,2.853,0,0,0-2.01,2.4c.069.311.156.661.234,1.1.337,1.809-.456,2.836-.711,2.836Z' transform='translate(4688.002 5171.001)' fill='%23fff'/%3E%3Cpath d='M-4605.7-5074.678h-4.357v-3.941A5.512,5.512,0,0,1-4613-5083.5a5.509,5.509,0,0,1,2.941-4.878v-3.943h4.357a5.524,5.524,0,0,1,5.2-3.68,5.521,5.521,0,0,1,5.2,3.68h4.357v3.943A5.507,5.507,0,0,1-4588-5083.5a5.511,5.511,0,0,1-2.941,4.882v3.941h-4.357a5.515,5.515,0,0,1-5.2,3.677A5.523,5.523,0,0,1-4605.7-5074.678Zm-2.151-13.065a4.387,4.387,0,0,0-2.943,4.242,4.39,4.39,0,0,0,2.943,4.246v2.373h2.956a4.439,4.439,0,0,0,4.4,4.046,4.434,4.434,0,0,0,4.4-4.046h2.956v-2.373a4.39,4.39,0,0,0,2.943-4.246,4.387,4.387,0,0,0-2.943-4.242v-2.377h-2.956a4.433,4.433,0,0,0-4.4-4.043,4.437,4.437,0,0,0-4.4,4.043h-2.956Zm7.3,10.847c-.245-.031-1.064-.962-.733-2.835.079-.441.166-.792.235-1.1.087-1.293-1.132-2.064-1.952-2.4-.333-.11.009.38-.567,1a1.253,1.253,0,0,1-1.678-.129,1.659,1.659,0,0,1-.224-1.845,1.57,1.57,0,0,1,1.61-.851c1.58-.01,2.7,1.543,3.112,2.866a3.532,3.532,0,0,0-.186-1.264c-.263-.861-1.326-1.6-1.326-3.507a4.171,4.171,0,0,1,1.756-3.155,4.2,4.2,0,0,1,1.756,3.155c0,1.9-1.073,2.646-1.336,3.507a3.532,3.532,0,0,0-.186,1.264c.41-1.323,1.532-2.866,3.112-2.866a1.571,1.571,0,0,1,1.61.851,1.659,1.659,0,0,1-.224,1.845,1.256,1.256,0,0,1-1.728.129c-.555-.651-.233-1.142-.555-1a2.853,2.853,0,0,0-2.01,2.4c.069.311.156.661.234,1.1.337,1.809-.456,2.836-.711,2.836Z' transform='translate(4638.002 5171.001)' fill='%23fff'/%3E%3Cpath id='Path_10999' d='M-4605.7-5074.678h-4.357v-3.941A5.512,5.512,0,0,1-4613-5083.5a5.509,5.509,0,0,1,2.941-4.878v-3.943h4.357a5.524,5.524,0,0,1,5.2-3.68,5.521,5.521,0,0,1,5.2,3.68h4.357v3.943A5.507,5.507,0,0,1-4588-5083.5a5.511,5.511,0,0,1-2.941,4.882v3.941h-4.357a5.515,5.515,0,0,1-5.2,3.677A5.523,5.523,0,0,1-4605.7-5074.678Zm-2.151-13.065a4.387,4.387,0,0,0-2.943,4.242,4.39,4.39,0,0,0,2.943,4.246v2.373h2.956a4.439,4.439,0,0,0,4.4,4.046,4.434,4.434,0,0,0,4.4-4.046h2.956v-2.373a4.39,4.39,0,0,0,2.943-4.246,4.387,4.387,0,0,0-2.943-4.242v-2.377h-2.956a4.433,4.433,0,0,0-4.4-4.043,4.437,4.437,0,0,0-4.4,4.043h-2.956Zm7.3,10.847c-.245-.031-1.064-.962-.733-2.835.079-.441.166-.792.235-1.1.087-1.293-1.132-2.064-1.952-2.4-.333-.11.009.38-.567,1a1.253,1.253,0,0,1-1.678-.129,1.659,1.659,0,0,1-.224-1.845,1.57,1.57,0,0,1,1.61-.851c1.58-.01,2.7,1.543,3.112,2.866a3.532,3.532,0,0,0-.186-1.264c-.263-.861-1.326-1.6-1.326-3.507a4.171,4.171,0,0,1,1.756-3.155,4.2,4.2,0,0,1,1.756,3.155c0,1.9-1.073,2.646-1.336,3.507a3.532,3.532,0,0,0-.186,1.264c.41-1.323,1.532-2.866,3.112-2.866a1.571,1.571,0,0,1,1.61.851,1.659,1.659,0,0,1-.224,1.845,1.256,1.256,0,0,1-1.728.129c-.555-.651-.233-1.142-.555-1a2.853,2.853,0,0,0-2.01,2.4c.069.311.156.661.234,1.1.337,1.809-.456,2.836-.711,2.836Z' transform='translate(4638.002 5121.001)' fill='%23fff'/%3E%3Cpath d='M-4605.7-5074.678h-4.357v-3.941A5.512,5.512,0,0,1-4613-5083.5a5.509,5.509,0,0,1,2.941-4.878v-3.943h4.357a5.524,5.524,0,0,1,5.2-3.68,5.521,5.521,0,0,1,5.2,3.68h4.357v3.943A5.507,5.507,0,0,1-4588-5083.5a5.511,5.511,0,0,1-2.941,4.882v3.941h-4.357a5.515,5.515,0,0,1-5.2,3.677A5.523,5.523,0,0,1-4605.7-5074.678Zm-2.151-13.065a4.387,4.387,0,0,0-2.943,4.242,4.39,4.39,0,0,0,2.943,4.246v2.373h2.956a4.439,4.439,0,0,0,4.4,4.046,4.434,4.434,0,0,0,4.4-4.046h2.956v-2.373a4.39,4.39,0,0,0,2.943-4.246,4.387,4.387,0,0,0-2.943-4.242v-2.377h-2.956a4.433,4.433,0,0,0-4.4-4.043,4.437,4.437,0,0,0-4.4,4.043h-2.956Zm7.3,10.847c-.245-.031-1.064-.962-.733-2.835.079-.441.166-.792.235-1.1.087-1.293-1.132-2.064-1.952-2.4-.333-.11.009.38-.567,1a1.253,1.253,0,0,1-1.678-.129,1.659,1.659,0,0,1-.224-1.845,1.57,1.57,0,0,1,1.61-.851c1.58-.01,2.7,1.543,3.112,2.866a3.532,3.532,0,0,0-.186-1.264c-.263-.861-1.326-1.6-1.326-3.507a4.171,4.171,0,0,1,1.756-3.155,4.2,4.2,0,0,1,1.756,3.155c0,1.9-1.073,2.646-1.336,3.507a3.532,3.532,0,0,0-.186,1.264c.41-1.323,1.532-2.866,3.112-2.866a1.571,1.571,0,0,1,1.61.851,1.659,1.659,0,0,1-.224,1.845,1.256,1.256,0,0,1-1.728.129c-.555-.651-.233-1.142-.555-1a2.853,2.853,0,0,0-2.01,2.4c.069.311.156.661.234,1.1.337,1.809-.456,2.836-.711,2.836Z' transform='translate(4688.002 5121.001)' fill='%23556B2F'/%3E%3C/g%3E%3C/svg%3E%0A"), linear-gradient(to bottom, ${colors.white}, ${colors.white})`,
  backgroundPosition: 'top left',

  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  color: colors.mainLighter,

  [s.sm_down]: {
    paddingTop: '6rem',
  },
  [s.md]: {
    paddingTop: '10rem',
  },
  [s.lg]: {
    paddingTop: '16rem',
  },
}

const sLogo = {
  svg: {
    width: 240,
    height: 'auto',
  },
  // filter: `drop-shadow( 1px 2px 24px ${colors.mainLighter})`,
  [s.sm_down]: {
    paddingBottom: '6rem',
    svg: {
      width: 180,
      height: 'auto',
    },
  },
  [s.md]: {
    paddingBottom: '10rem',
  },
}

export const sInnerSection = {
  // backgroundColor: colors.mainLighter.concat(alpha[8]),
  width: '100%',
  maxWidth: 1280,
  margin: '0 auto',
  boxSizing: 'border-box',
  contentVisibility: 'auto',
  containIntrinsicSize: '1080px',
  [s.xs]: {
    padding: '0rem',
    paddingBottom: '6rem',
  },
  [s.sm]: {
    padding: '0 4rem',
    paddingBottom: '6rem',
  },
  [s.md]: {
    padding: '0 4rem',
    paddingBottom: '10rem',
  },
  [s.lg]: {
    paddingBottom: '16rem',
  },
}

const sHeadline = {
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  [s.sm_down]: {
    padding: '0 2rem',
    paddingBottom: '4rem',
  },
  [s.xs]: {
    padding: 0,
    paddingBottom: '4rem',
  },
  [s.md]: {
    paddingBottom: '6rem',
  },
  '.headline-title': {
    fontFamily: 'Butler',
    [s.sm_down]: {
      fontSize: '2.25rem',
    },
    [s.xs]: {
      padding: '0 2rem',
    },
    [s.md]: {
      fontSize: '3rem',
    },
    // textShadow: `1px 2px 24px ${colors.text.concat(alpha[100])}`,
  },
  '.headline-desc': {
    [s.sm_down]: {
      fontSize: '0.875rem',
      paddingTop: '2rem',
      width: '100%',
      margin: '0 auto',
    },
    // [s.xs]: {
    //   margin: '0 2rem',
    // },
    [s.xs]: {
      maxWidth: 'calc(100vw - 4rem)',
    },
    [s.md]: { paddingTop: '4rem', margin: '0 auto' },
    [s.lg]: {
      width: '75%',
    },
    opacity: 0.6,
    fontWeight: 300,
  },
}

const sTextContainer = {
  fontWeight: 300,
  display: 'flex',
  flexWrap: 'wrap',
  padding: '2rem',
  background: `linear-gradient(to right, ${colors.mainLighter.concat(
    alpha[16]
  )}, ${colors.mainLighter.concat(alpha[8])})`,
  marginBottom: '4rem',
  lineHeight: '2em',
  p: {
    padding: '2rem',
  },
  [s.xs]: {
    padding: '1rem',
    margin: '0 1rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    p: {
      padding: '1rem 1rem 1rem',
      marginBottom: '1rem',
    },
  },

  large: {
    fontSize: '1.125rem',
  },
}

const sDotGroupNew = {
  [s.sm_down]: {
    bottom: '2rem',
    right: '2rem',
  },
  [s.xs]: {
    bottom: '1rem',
    right: '50%',
    transform: 'translateX(50%)',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [s.md]: {
    bottom: '2.5rem',
    right: '4rem',
  },
}

const sDimmer = {
  pointerEvents: 'none',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: `linear-gradient(to bottom, ${colors.black.concat(
    alpha[0]
  )} 60%, ${colors.black.concat(alpha[60])})`,
}

const sCarouselTitle = {
  color: '#ffffff',
  textTransform: 'uppercase',
  fontSize: '1.25rem',
  letterSpacing: '0.25em',
  [s.xs]: {
    fontSize: '0.875rem',
  },
}

export default Presentation
